// Buying a playlist: tonight's set (bid box, bids only up) or a Storefront v2
// shelf item (fixed price), the free one-tap claim, the debounced
// PaymentIntent, the wallet-direct tap and the success tail. Deliberate twin
// of hooks/useTipCheckout. Opening buy mode closes a tip in progress, so the
// tip hook's setters come in. Split out of pages/RequestPage.tsx.
import { takesMoney } from "../utils/takesMoney";
import { useEffect, useMemo, useRef, useState } from "react";
import type { PaymentRequest as StripePaymentRequest } from "@stripe/stripe-js";
import { Capacitor } from "@capacitor/core";
import { paymentsApi } from "../services/payments";
import { maxPriceFor } from "../utils/payinConfig";
import { calculateFeeBreakdown, round2 } from "../utils/feeCalculator";
import { ShopPlaylist } from "../types/User";
import { STRIPE_PK, stripePromise, customerCopyForPayError } from "../lib/fanCheckout";
import type { TipCheckout } from "./useTipCheckout";
import { t } from "../i18n";

type Args = {
  dj: any;
  viewDj: any;
  isProfilePreview: boolean;
  clientNickname: string;
  clientInstagram: string;
  tip: TipCheckout;
};

export function useBuyCheckout({ dj, viewDj, isProfilePreview, clientNickname, clientInstagram, tip }: Args) {
  const { setTipMode, setTipShowPay, setTipShowCard, setTipPayError } = tip;
  // Storefront v2 — which shelf playlist is being bought (null = tonight's
  // set, the historical path), and the shelf sheet's open state.
  const [buyShopId, setBuyShopId] = useState<string | null>(null);
  const [shopSheetOpen, setShopSheetOpen] = useState(false);
  const [freeBusy, setFreeBusy] = useState(false);

  // Buy Playlist (Setlist sale) — tonight's set uses the same PLACE YOUR BID
  // box as song requests (founder decision): min-locked input, bids only go UP.
  const [buyMode, setBuyMode] = useState(false);
  const [buyAmount, setBuyAmount] = useState("");
  const [buyBidInfoOpen, setBuyBidInfoOpen] = useState(false);
  const [buyBidFocused, setBuyBidFocused] = useState(false);
  const [buyBidPlaceholder, setBuyBidPlaceholder] = useState("");
  const [buyOrderId, setBuyOrderId] = useState<string | null>(null);
  const [buyClientSecret, setBuyClientSecret] = useState<string | null>(null);
  const [buyShowPay, setBuyShowPay] = useState(false);
  const [buyShowCard, setBuyShowCard] = useState(false);
  const [buyProcessing, setBuyProcessing] = useState(false);
  const [buyPayError, setBuyPayError] = useState<string | null>(null);
  // Payment options reveal only after the "Buy Playlist · $X" tap (the mock's
  // commitment point) — the intent itself arms in the background like a tip.
  const [buyCtaTapped, setBuyCtaTapped] = useState(false);
  const buyDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /** Same outside-tap close for the playlist bid box's popover. */
  useEffect(() => {
    if (!buyBidInfoOpen) return;
    const handler = (e: Event) => {
      const target = e.target as HTMLElement;
      if (target.closest(".rq-tier-info-btn") || target.closest(".rq-tier-info-popover")) return;
      setBuyBidInfoOpen(false);
    };
    document.addEventListener("mousedown", handler);
    document.addEventListener("touchstart", handler, { passive: true });
    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("touchstart", handler);
    };
  }, [buyBidInfoOpen]);

  /** === Buy Playlist (Setlist sale) — price + intent === */
  // Mirror of the server's purchase floor: the DJ's setlist price, never
  // advertised below the per-currency setlist floor (legacy teaser-era
  // prices could sit under it — the server floors the charge, so the chip
  // must never promise less).
  // FLOOR REMOVED (founder: "can be any price even free"). The old
  // Math.max(p, setlistFloor) here mirrored a server floor that no longer
  // exists — keeping it would advertise MORE than the DJ actually charges.
  // The DJ's saved number is the price, full stop. 0 is a real price, so
  // "has a price" is an explicit null check, never a truthiness test.
  const setlistPriceRaw = viewDj?.setlistPrice;
  const setlistHasPrice =
    setlistPriceRaw !== null &&
    setlistPriceRaw !== undefined &&
    Number.isFinite(Number(setlistPriceRaw)) &&
    Number(setlistPriceRaw) >= 0;
  const setlistChipPrice = setlistHasPrice ? round2(Number(setlistPriceRaw)) : 0;

  // App Review 3.1.1 (rejection 2026-09-15): a playlist is digital content
  // delivered inside the app, which Apple requires to be sold through in-app
  // purchase. Playlist SALES are therefore parked inside the native app only.
  // The web keeps selling them unchanged, and a fan who already bought one
  // still opens it at /playlist/:receiptId — owning and playing what you
  // bought is fine, only the point of sale is gated. Gate both products here
  // (tonight's set AND the Storefront v2 shelf); every route into checkout
  // runs through the banner that reads these two flags.
  // Free tier: a talent who can't take money yet sells nothing either.
  const digitalSalesAllowed = !Capacitor.isNativePlatform() && takesMoney(viewDj);
  const setlistOn = !!viewDj?.setlistSaleEnabled && setlistHasPrice && digitalSalesAllowed;

  /** === Storefront v2 — the shelf === */
  // Gated on the server capability, never on the array's presence.
  const storefrontV2 = (viewDj as any)?.storefrontV2 === true;
  const shopPlaylists: ShopPlaylist[] = useMemo(
    () =>
      digitalSalesAllowed && storefrontV2 && Array.isArray(viewDj?.shopPlaylists)
        ? viewDj!.shopPlaylists!
        : [],
    [digitalSalesAllowed, storefrontV2, viewDj]
  );
  // A DJ with an empty shelf keeps TODAY's page exactly: one banner straight
  // into checkout. The shelf only appears once a slot is actually filled.
  const hasShelf = shopPlaylists.length > 0;
  // The product being bought: null = tonight's set (the historical path).
  const buyShopItem = useMemo(
    () => (buyShopId ? shopPlaylists.find((p) => p.id === buyShopId) || null : null),
    [buyShopId, shopPlaylists]
  );
  /** The price of whatever is currently being bought. */
  const buyProductPrice = buyShopItem ? Number(buyShopItem.price ?? 0) : setlistChipPrice;
  const buyIsFree = buyProductPrice === 0;
  /** Cheapest thing on the page — the banner's "from" anchor. */
  const shelfFromPrice = useMemo(() => {
    const prices = shopPlaylists.map((p) => Number(p.price ?? 0));
    if (setlistOn) prices.push(setlistChipPrice);
    return prices.length ? Math.min(...prices) : 0;
  }, [shopPlaylists, setlistOn, setlistChipPrice]);

  // Bids only go UP — but ONLY on tonight's set. A shelf item is a fixed
  // price (founder Q4): generosity lives on tips and tonight's set, and a
  // shop where the price moves is just confusing.
  const buyBase = (() => {
    if (buyShopItem) return round2(buyProductPrice);
    const raw = Number(buyAmount);
    if (!Number.isFinite(raw) || raw < setlistChipPrice) return setlistChipPrice;
    // Fraud door: tonight's-set bids share the DJ's request cap (server
    // /create-setlist-intent refuses above it).
    return Math.min(round2(raw), maxPriceFor(viewDj?.currency, viewDj?.capsLifted));
  })();
  const buyTotal = calculateFeeBreakdown(buyBase, viewDj?.currency).customerTotal;

  // Tonight's-set entry is the same PLACE YOUR BID box as song requests
  // (founder — "identical mechanism on both"): locked to the DJ's
  // setlist price, bids only go UP. buyBase above does the clamping. The
  // emoji tiles + the parked SHOW_BUY_CUSTOM button they replaced are gone.

  const openBuyMode = (shopId: string | null = null) => {
    if (isProfilePreview) return;
    setBuyShopId(shopId);
    setShopSheetOpen(false);
    setTipMode(false);
    setTipShowPay(false);
    setTipShowCard(false);
    setTipPayError(null);
    setBuyMode(true);
    // Shop items carry their fixed price. Tonight's set starts EMPTY so the
    // bid input shows the setlist price as its faint placeholder (same feel
    // as the song box) — buyBase falls back to the price until a higher bid.
    setBuyAmount(shopId ? String(shopPlaylists.find((p) => p.id === shopId)?.price ?? 0) : "");
    setBuyBidInfoOpen(false);
    setBuyBidFocused(false);
    setBuyBidPlaceholder("");
    setBuyOrderId(null);
    setBuyClientSecret(null);
    setBuyShowPay(false);
    setBuyShowCard(false);
    setBuyPayError(null);
    setBuyCtaTapped(false);
  };

  const closeBuyMode = () => {
    setBuyMode(false);
    setBuyBidInfoOpen(false);
    setBuyBidFocused(false);
    setBuyShowPay(false);
    setBuyShowCard(false);
    setBuyPayError(null);
    setBuyCtaTapped(false);
    window.scrollTo(0, 0);
  };

  /* ── Buy Playlist → wallet-direct v2 (founder, round 2): the tap must
     NEVER wait on arming. ONE PaymentRequest per sheet-open (one
     canMakePayment per instance — boost's rule), kept current via
     update({total}) as the fan taps tiles; the tap does update → show()
     synchronously (Safari gesture rule) and resolves the PI while the
     sheet is already up — reusing the pre-minted secret when it matches
     the tapped amount, minting a fresh one otherwise (boost's
     intentPromise pattern). Handlers attach ONCE; per-tap state lives in
     buyTapCtxRef. Every failure path still lands in the two-step reveal,
     which is literally the flow that shipped before. */
  const buyWalletRef = useRef<{
    pr: StripePaymentRequest;
    applePay: boolean;
    lastCents: number;
  } | null>(null);
  const buyWalletSeqRef = useRef(0);
  const [buyWalletReady, setBuyWalletReady] = useState(false);
  // Which base amount the debounce-minted secret was created for — the tap
  // reuses it only on an exact match.
  const buySecretBaseRef = useRef<number | null>(null);
  const buyTapCtxRef = useRef<{
    secretPromise: Promise<{ clientSecret: string; orderId: string }>;
    cancelled: boolean;
    succeeded: boolean;
    uiSettled: boolean;
  } | null>(null);

  useEffect(() => {
    const seq = ++buyWalletSeqRef.current; // stales any in-flight arm
    buyWalletRef.current = null;
    buyTapCtxRef.current = null;
    setBuyWalletReady(false);
    // A free playlist never opens a wallet — Stripe rejects a zero-amount
    // payment request, and there is nothing to pay for anyway.
    if (isProfilePreview || !buyMode || !stripePromise || buyIsFree) return;
    const initialCents = Math.round(buyTotal * 100);
    (async () => {
      try {
        const stripe = await stripePromise;
        if (!stripe || seq !== buyWalletSeqRef.current) return;
        const pr = stripe.paymentRequest({
          country: "US",
          currency: String(viewDj?.currency || "usd").toLowerCase(),
          total: { label: "Request App", amount: initialCents },
          // Delivery goes out by email — the wallet pre-fills the payer's,
          // no typing (same policy as the express buttons' emailRequired).
          requestPayerEmail: true,
          // Link opens its own modal, not a wallet sheet — surprising from
          // these buttons. Link stays available in the two-step fallback.
          disableWallets: ["link"],
        });
        const avail = await pr.canMakePayment();
        if (seq !== buyWalletSeqRef.current) return;
        if (!avail) return; // no wallet on this device → classic flow

        // Handlers attach ONCE per instance — a declined wallet card fires
        // BOTH paths (complete("fail") → fan dismisses → "cancel"), so
        // uiSettled makes whoever reacts first the only fallback writer.
        pr.on("cancel", () => {
          const ctx = buyTapCtxRef.current;
          if (!ctx) return;
          ctx.cancelled = true;
          if (!ctx.succeeded && !ctx.uiSettled) {
            ctx.uiSettled = true;
            setBuyCtaTapped(true);
          }
        });

        pr.on("paymentmethod", async (ev) => {
          const ctx = buyTapCtxRef.current;
          if (!ctx) {
            try { ev.complete("fail"); } catch {}
            return;
          }
          let clientSecret: string;
          let orderId: string;
          try {
            const d = await ctx.secretPromise;
            clientSecret = d.clientSecret;
            orderId = d.orderId;
          } catch (err: any) {
            try { ev.complete("fail"); } catch {}
            if (!ctx.uiSettled) {
              ctx.uiSettled = true;
              setBuyCtaTapped(true);
              setBuyPayError(customerCopyForPayError(err, dj?.name || ""));
            }
            return;
          }
          if (ctx.cancelled) return; // sheet gone; cancel owns the fallback
          try {
            const stripeNow = stripePromise ? await stripePromise : null;
            if (!stripeNow || ctx.cancelled) {
              try { ev.complete("fail"); } catch {}
              return;
            }
            const result = await stripeNow.confirmCardPayment(
              clientSecret,
              { payment_method: ev.paymentMethod.id },
              { handleActions: false }
            );
            if (result.error) {
              try { ev.complete("fail"); } catch {}
              if (!ctx.uiSettled) {
                ctx.uiSettled = true;
                setBuyCtaTapped(true);
                setBuyPayError(result.error.message || t("useBuyCheckout.paymentError"));
              }
              return;
            }
            ctx.succeeded = true;
            ctx.uiSettled = true;
            try { ev.complete("success"); } catch {}
            let pi = result.paymentIntent;
            if (pi && pi.status === "requires_action") {
              // 3DS (>$30/€30 policy) — the sheet is closed; Stripe opens
              // its own authentication modal.
              const next = await stripeNow.confirmCardPayment(clientSecret);
              if (next.error || !next.paymentIntent) {
                ctx.succeeded = false;
                setBuyCtaTapped(true);
                setBuyPayError(next.error?.message || t("useBuyCheckout.paymentError"));
                return;
              }
              pi = next.paymentIntent;
            }
            await buySucceeded(orderId, pi?.id);
          } catch {
            try { ev.complete("fail"); } catch {}
            if (!ctx.succeeded && !ctx.uiSettled) {
              ctx.uiSettled = true;
              setBuyCtaTapped(true);
              setBuyPayError(t("useBuyCheckout.paymentError"));
            }
          }
        });

        buyWalletRef.current = { pr, applePay: !!avail.applePay, lastCents: initialCents };
        setBuyWalletReady(true);
      } catch {
        if (seq === buyWalletSeqRef.current) {
          buyWalletRef.current = null;
          setBuyWalletReady(false);
        }
      }
    })();
    // Armed once per entry into buy mode, same reasoning as the tip wallet.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buyMode, isProfilePreview, buyIsFree]);

  // Keep the (not-showing) sheet's total in step with the selected tile —
  // update() is allowed any time the sheet is closed, and costs nothing.
  useEffect(() => {
    const armed = buyWalletRef.current;
    if (!armed) return;
    const cents = Math.round(buyTotal * 100);
    if (cents === armed.lastCents) return;
    try {
      armed.pr.update({ total: { label: "Request App", amount: cents } });
      armed.lastCents = cents;
    } catch {}
  }, [buyTotal, buyWalletReady]);

  // Shared tail of a successful setlist payment — used by the wallet-direct
  // path and by InlinePayOptions' onPaymentSuccess.
  const buySucceeded = async (orderId: string, piId?: string) => {
    // Storefront v2: a shelf sale is captured AND delivered inside
    // confirm-intent, so the response can hand us the receipt to open. Send
    // the fan straight to their playlist — making them sit on a "waiting for
    // the DJ" screen for something they already own is the one thing instant
    // delivery exists to avoid.
    let deliveredReceipt: string | null = null;
    try {
      const data = await paymentsApi.confirmIntent(orderId, piId);
      if (data?.shopDelivered && data.receiptId) deliveredReceipt = data.receiptId;
    } catch (e) {
      console.error("confirm setlist error", e);
    }
    setBuyClientSecret(null);
    setBuyShowPay(false);
    setBuyMode(false);
    window.location.href = deliveredReceipt
      ? `/playlist/${encodeURIComponent(deliveredReceipt)}`
      : `/fan/requests?order=${encodeURIComponent(orderId)}`;
  };

  /** Free playlists (founder: "can be any price even free").
   *  Stripe has no zero-amount charge, so this is a single POST with no
   *  payment sheet at all: the server records the order and, for a shelf
   *  item, delivers it in the same request. */
  const claimFreePlaylist = async () => {
    if (isProfilePreview || freeBusy || !dj?.slug) return;
    setFreeBusy(true);
    setBuyPayError(null);
    try {
      const data = await paymentsApi.claimFreeSetlist({
          djSlug: dj.slug,
          amount: 0,
          clientNickname: clientNickname || undefined,
          clientInstagram: clientInstagram || undefined,
          ...(buyShopId ? { shopPlaylistId: buyShopId } : {}),
      });
      setBuyMode(false);
      window.location.href = data.delivered
        ? `/playlist/${encodeURIComponent(data.receiptId)}`
        : `/fan/requests?order=${encodeURIComponent(data.orderId)}`;
    } catch (e: any) {
      setBuyPayError(customerCopyForPayError(e, dj?.name || ""));
      setFreeBusy(false);
    }
  };

  /** The setlist purchase tap ("Get the setlist · X"). Settles the pad,
      opens the wallet sheet synchronously, then resolves the PI while the
      sheet is up. Delivery to Spotify / Apple Music happens on the
      delivered page — it is not what the fan is choosing here. */
  const startBuyPurchase = () => {
    // buyBase already floor-clamps whatever sits in the custom input.
    const base = buyBase;
    const total = calculateFeeBreakdown(base, viewDj?.currency).customerTotal;

    const armed = buyWalletRef.current;
    if (!armed) {
      setBuyCtaTapped(true); // no wallet on this device → classic reveal
      return;
    }
    const cents = Math.round(total * 100);
    if (cents !== armed.lastCents) {
      // Sheet total must equal what the PI will charge — sync before show.
      try {
        armed.pr.update({ total: { label: "Request App", amount: cents } });
        armed.lastCents = cents;
      } catch (e) {
        console.error("wallet update() failed:", e);
        setBuyCtaTapped(true);
        return;
      }
    }
    let shown = false;
    try {
      armed.pr.show(); // synchronously inside the gesture (Safari rule)
      shown = true;
    } catch (e) {
      console.error("wallet show() failed:", e);
    }
    if (!shown) {
      setBuyCtaTapped(true);
      return;
    }

    // PI: reuse the pre-minted secret when it matches this tap's amount,
    // else mint now — the sheet is already up, boost-style.
    const fresh =
      buyClientSecret && buyOrderId && buySecretBaseRef.current === base;
    const secretPromise = fresh
      ? Promise.resolve({ clientSecret: buyClientSecret as string, orderId: buyOrderId as string })
      : paymentsApi.createSetlistIntent({
            djSlug: dj?.slug,
            amount: base,
            clientNickname: clientNickname || undefined,
            clientInstagram: clientInstagram || undefined,
            // Storefront v2: the server reads the price off the shelf doc
            // and ignores `amount` on this branch — the client can never
            // name the price of a shop item.
            ...(buyShopId ? { shopPlaylistId: buyShopId } : {}),
          });
    secretPromise.catch(() => {}); // consumed by whichever handler runs first
    buyTapCtxRef.current = {
      secretPromise,
      cancelled: false,
      succeeded: false,
      uiSettled: false,
    };
  };

  // Same debounced-intent recipe as the tip above; the server floors the
  // amount again (max of the DJ's price and the currency floor) and holds the
  // card only — capture happens at delivery.
  useEffect(() => {
    if (isProfilePreview) return;
    if (!buyMode || !dj?.slug || (!stripePromise && STRIPE_PK)) return;
    // A free playlist never touches Stripe — no intent, no payment sheet.
    // Its own CTA posts once and navigates (claimFreePlaylist below).
    if (buyIsFree) return;
    if (!buyShopItem && (!setlistOn || buyBase <= 0)) return;

    setBuyShowPay(false);
    setBuyClientSecret(null);
    setBuyShowCard(false);
    setBuyPayError(null);

    const reqId = (window.crypto?.randomUUID?.() || Math.random().toString(36)).replace(/-/g, "");
    (window as any).__rq_last_buy = reqId;

    if (buyDebounceRef.current) clearTimeout(buyDebounceRef.current);
    buyDebounceRef.current = setTimeout(async () => {
      try {
        const data = await paymentsApi.createSetlistIntent({
            djSlug: dj.slug,
            amount: buyBase,
            clientNickname: clientNickname || undefined,
            clientInstagram: clientInstagram || undefined,
            ...(buyShopId ? { shopPlaylistId: buyShopId } : {}),
        });
        if ((window as any).__rq_last_buy !== reqId) return;
        buySecretBaseRef.current = buyBase; // which amount this secret is for
        setBuyClientSecret(data.clientSecret);
        setBuyOrderId(data.orderId);
        setBuyShowPay(true);
        setBuyPayError(null);
        sessionStorage.setItem("lastDjSlug", dj.slug);
      } catch (e: any) {
        console.error("Setlist intent error", e);
        // The shared copy map's not_activated line says "tips" — wrong word
        // for this surface.
        setBuyPayError(
          e?.response?.data?.code === "not_activated"
            ? `${dj?.name || "This performer"} isn't selling playlists right now.`
            : customerCopyForPayError(e, dj?.name || "")
        );
      }
    }, 400);

    return () => {
      if (buyDebounceRef.current) clearTimeout(buyDebounceRef.current);
    };
    // Keyed on the amount and the item, not on the free-text fields.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buyMode, dj?.slug, buyBase, setlistOn, stripePromise, isProfilePreview, buyShopId, buyIsFree, buyShopItem]);

  return {
    buyShopId,
    shopSheetOpen,
    setShopSheetOpen,
    freeBusy,
    buyMode,
    buyAmount,
    setBuyAmount,
    buyBidInfoOpen,
    setBuyBidInfoOpen,
    buyBidFocused,
    setBuyBidFocused,
    buyBidPlaceholder,
    setBuyBidPlaceholder,
    buyOrderId,
    buyClientSecret,
    buyShowPay,
    buyShowCard,
    setBuyShowCard,
    buyProcessing,
    setBuyProcessing,
    buyPayError,
    setBuyPayError,
    buyCtaTapped,
    setlistChipPrice,
    setlistOn,
    shopPlaylists,
    hasShelf,
    buyShopItem,
    buyProductPrice,
    buyIsFree,
    shelfFromPrice,
    buyTotal,
    openBuyMode,
    closeBuyMode,
    buySucceeded,
    claimFreePlaylist,
    startBuyPurchase,
  };
}

export type BuyCheckout = ReturnType<typeof useBuyCheckout>;
